import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, ExternalLink, FileSpreadsheet, Wrench, Receipt } from "lucide-react";

const money = (n) => `$${(parseFloat(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CATEGORY_META = {
  invoices:    { label: "Supplier Invoices", icon: FileSpreadsheet, accent: "text-sky-700 bg-sky-50 border-sky-200" },
  work_orders: { label: "Work Orders",       icon: Wrench,          accent: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  other:       { label: "Other Expenses",    icon: Receipt,         accent: "text-pink-800 bg-pink-50 border-pink-200" },
};

export default function CashFlowDrillDialog({ open, onClose, month, monthLabel, category }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !month || !category) return;
    setLoading(true);
    api.get(`/dashboard/cash-flow/detail?month=${month}&category=${category}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [], total: 0, count: 0 }))
      .finally(() => setLoading(false));
  }, [open, month, category]);

  const meta = CATEGORY_META[category] || CATEGORY_META.other;
  const Icon = meta.icon;

  const go = (row) => {
    onClose();
    if (row.route) navigate(row.route);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cash-flow-drilldown">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border ${meta.accent}`}>
              <Icon className="w-3.5 h-3.5" /> {meta.label}
            </span>
            <span className="text-slate-500 font-normal">{monthLabel}</span>
          </DialogTitle>
          {data && (
            <p className="text-xs text-slate-500 mt-1">
              {data.count} record{data.count === 1 ? "" : "s"} · Total {money(data.total)}
            </p>
          )}
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
        ) : !data || data.rows.length === 0 ? (
          <div className="py-10 text-center text-slate-400 text-sm">No records in this slice.</div>
        ) : (
          <Table data-testid="cash-flow-drilldown-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Date</TableHead>
                <TableHead>{category === "invoices" ? "Vendor" : "Vendor / Category"}</TableHead>
                <TableHead>Detail</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow key={r.id} data-testid={`cash-flow-row-${r.id}`} className="hover:bg-slate-50/60 cursor-pointer" onClick={() => go(r)}>
                  <TableCell className="font-mono text-xs whitespace-nowrap">{r.date}</TableCell>
                  <TableCell className="text-sm font-medium text-slate-800">{r.label || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-[240px] truncate" title={r.detail}>{r.detail || "—"}</TableCell>
                  <TableCell className="font-mono text-sm font-semibold text-right">{money(r.amount)}</TableCell>
                  <TableCell><ExternalLink className="w-3.5 h-3.5 text-slate-300" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
